import { Spinner } from "react-bootstrap";
import {
  Localization,
  AppMessagesContext,
  actionSetInfoMessage,
  actionSetProgressMessage,
  SettingsManager,
} from "@viamap/viamap2-common";
import "./PropertyInformationDialog.css";
import { Feature } from "src/states/ApplicationState";
import {
  ProjectContext,
  handleToBeAdded,
} from "src/states/ProjectState";
import { AddressInterface } from "src/managers/AddressInterface";
import { ProtectedFeature } from "./ProtectedFeature";
import { PropertyInfoView } from "src/propertyInfoTemplates/PropertyInfoView";
import { FiCopy, FiPrinter } from "react-icons/fi";
import { MitStorage } from "src/managers/MitStorage";
import { ViewButtonCheck, ViewButtonDropdown, ViewButtonIcon, ViewButtonText } from "src/propertyInfoTemplates/PropertyComponents";
import { ExportData } from "src/managers/ExportData";
import { PropertyData } from "src/propertyInfoTemplates/PropertyInfoCollectorTypes";
import { SheetFunc } from "src/managers/SheetFunc";
import { ADModalBodyProp, AdvancedDragModal } from "src/componentsUtils/AdvancedDragModal";
import { useState, useContext, useEffect } from "react";
import { closeWindow, ManagedWindow, WindowId } from "src/WindowManager/WindowState";
import { useWindowContext } from "src/WindowManager/useWindowContext";
import { Document } from "iconsax-react";
import { PropertyInfoInterface } from "src/managers/PropertyInfoInterface";
import { parse as WKTparse } from "wkt";
import { GenerateGeomUtils } from "src/managers/GenerateGeomUtils";


type Props = {
  managedWindow: ManagedWindow;
  callBackOnClose: () => void;
  labelLatLng?: { latlng?: { lat: number; lng: number } };
  bfeNr?: number | string;
  allowExtendedInformation: boolean;
  cols?: number;
};

export function PropertyInformationDialog(props: Props) {
  const [propertyDefaultCollapsed, setPropertyDefaultCollapsed] =
    useState(MitStorage.getValue<Boolean>("PropertyAutoColapsed"));
  const { dispatch: appMessageDispatch } = useContext(AppMessagesContext);
  const { dispatch: windowDispatch } = useWindowContext();
  const { state: projState, dispatch: projDispatch } =
    useContext(ProjectContext);

  const [isLoading, setIsLoading] = useState(true);
  const [propertyData, setPropertyData] = useState<any>();
  const [effectiveLabelLatLng, setEffectiveLabelLatLng] = useState<
    { latlng?: { lat: number; lng: number } } | undefined
  >(props.labelLatLng);

  useEffect(() => {
    setIsLoading(true);
  }, [props.labelLatLng, props.bfeNr]);

  useEffect(() => {
    let cancelled = false;

    async function resolveLatLngFromBfe(bfe: number | string): Promise<{ lat: number; lng: number } | null> {
      const bfeNum = typeof bfe === "string" ? parseInt(bfe, 10) : bfe;
      if (!Number.isFinite(bfeNum)) return null;
      try {
        const res = await PropertyInfoInterface.LookupByBFE(bfeNum);
        const geometri =
          (res as any)?.SamletFastEjendom?.features?.[0]?.properties?.jordstykke?.[0]?.properties?.centroide?.[0]?.geometri ??
          (res as any)?.SamletFastEjendom?.features?.[0]?.properties?.jordstykke?.[0]?.properties?.centroide?.geometri;

        if (geometri) {
          const tmp: any = WKTparse(geometri);
          const x = tmp?.coordinates?.[0];
          const y = tmp?.coordinates?.[1];
          if (typeof x === "number" && typeof y === "number") {
            return GenerateGeomUtils.convertFromUTMtoLatLng({ x, y });
          }
        }
      } catch (e) {
        // fall through to other methods
      }

      try {
        const EBR = await PropertyInfoInterface.EBRadresseForBFE(String(bfeNum));
        const geometri =
          EBR?.features?.[0]?.properties?.adresse?.[0]?.husnummer?.adgangspunkt?.position ??
          EBR?.features?.[0]?.properties?.husnummer?.[0]?.adgangspunkt?.position;
        if (geometri) {
          const tmp: any = WKTparse(geometri);
          const x = tmp?.coordinates?.[0];
          const y = tmp?.coordinates?.[1];
          if (typeof x === "number" && typeof y === "number") {
            return GenerateGeomUtils.convertFromUTMtoLatLng({ x, y });
          }
        }
      } catch (e) {
        // fall through to other methods
      }

      return null;
    }

    async function resolve() {
      if (props.labelLatLng?.latlng?.lat && props.labelLatLng?.latlng?.lng) {
        setEffectiveLabelLatLng(props.labelLatLng);
        return;
      }

      if (props.bfeNr === undefined || props.bfeNr === null) {
        setEffectiveLabelLatLng(undefined);
        setIsLoading(false);
        return;
      }

      const coords = await resolveLatLngFromBfe(props.bfeNr);
      if (cancelled) return;

      if (!coords) {
        appMessageDispatch(actionSetInfoMessage(Localization.getText("Could not resolve coordinates for BFE")));
        setEffectiveLabelLatLng(undefined);
        setIsLoading(false);
        return;
      }

      setEffectiveLabelLatLng({ latlng: coords });
    }

    resolve();

    return () => {
      cancelled = true;
    };
  }, [props.labelLatLng, props.bfeNr, appMessageDispatch]);

  function RemberCollapseSettings(e: any, val) {
    MitStorage.setValue<Boolean>("PropertyAutoColapsed", !val);
  }

  async function handleExportToExcel() {
    let wb = await (new ExportData()).createPropertyDataExcelExport(
      "Property Export",
      new Date(),
      propertyData,
      (progress: number) => {
        appMessageDispatch(actionSetProgressMessage("Export", Math.round(100 * progress / 1)));
      });
    let blob = SheetFunc.createBlob(wb);
    let fileName = "PropertyData" + (propertyData?.BBRRel?.bfeNummer || "");
    fileName += ".xlsx";
    ExportData.downloadBlob(fileName, blob);
  }

  let fold = (
    <ViewButtonCheck
      checked={!propertyDefaultCollapsed}
      className="no-print"
      onCheck={(e, val) => {
        RemberCollapseSettings(e, val);
        setPropertyDefaultCollapsed(val);
      }}
    >
      {Localization.getText("Expanded")}
    </ViewButtonCheck>
  );

  return (
    <AdvancedDragModal
      title={"Ejendom"}
      window={props.managedWindow}
      allowUserMiniMize={true}
      allowFullscreen={true}
      PosDefault={{ width: "800px", height: "calc(100% - 50px)", top: "50px", left: "50%", transform: "translateX(-50%)" }}
      PosDefaultNS={{ width: "calc( 100% - 30px )", height: "calc(100% - 50px)", top: "50px", left: "15px" }}
      saveKey="PropertyInfo"
      PositionToSave={{ width: true, height: true, top: true, left: true }}
      variant="NSPadding"
      onClose={() => {
        windowDispatch(closeWindow(WindowId.PropertyInformationDialog))
      }}
      subHeaderContent={
        <>
          <ProtectedFeature
            feature={Feature.LinkToMunicipalityWebDocuments}
            contentsIfNoAccess={<></>}
          >
            <ViewButtonDropdown hideEmpty className="ViewButtonDropdownIcon" onSelect={(e) => {
              window.open(e, "_blank");
            }} options={function () {
              let muni = parseInt(propertyData?.DARAdresser?.flat()?.[0]?.husnummer.kommuneinddeling.kommunekode ?? "9999");
              return SettingsManager.getSystemSetting("propertyExternalMunicipalityDocuments")?.filter((a) => {
                return a.municipality.includes(muni);
              }).map((a) => {
                return { text: a.name, value: a.url };
              }) || []
            }()}
            >
              <Document size="24" />
            </ViewButtonDropdown>
          </ProtectedFeature>
          <ProtectedFeature
            feature={Feature.AddToProjectUnassignedList}
            contentsIfNoAccess={<></>}
          >
            <ViewButtonText
              className="no-print"
              onClick={async () => {
                if (projState.isEmpty) {
                  appMessageDispatch(
                    actionSetInfoMessage(
                      Localization.getText(
                        "Please visit the Project tabs, an action is needed!"
                      )
                    )
                  );
                  return;
                }
                if (
                  effectiveLabelLatLng?.latlng?.lat &&
                  effectiveLabelLatLng?.latlng?.lng
                ) {
                  let resp = await AddressInterface.reverseGeocodeFull(
                    effectiveLabelLatLng?.latlng.lat,
                    effectiveLabelLatLng?.latlng.lng
                  );
                  let addrData = await resp.json();
                  let jordstykke = await (
                    await fetch(addrData.jordstykke.href)
                  ).json();

                  let fullAddress = addrData.adressebetegnelse;
                  let tmp = fullAddress.split(",");
                  let streetAndNumber = tmp[0] || fullAddress;
                  let zipAndCity =
                    addrData.postnummer.nr + " " + addrData.postnummer.navn;

                  projDispatch(
                    handleToBeAdded([{ bfeNr: jordstykke.bfenummer, name: streetAndNumber + ", " + zipAndCity, latLng: [effectiveLabelLatLng?.latlng.lat, effectiveLabelLatLng?.latlng.lng] }])
                  );
                  return;
                }
              }}
            >
              {Localization.getText("Add to Project")}
            </ViewButtonText>
          </ProtectedFeature>
          <ProtectedFeature feature={Feature.ExplorerToExcel} contentsIfNoAccess={<></>} >
            {isLoading ? <></>
              :
              <ViewButtonIcon
                disabled={false}
                title={Localization.getText("Export to Excel")} className="no-print" onClick={() => handleExportToExcel()} ><img src="./images/NewSecSVG/excel.svg"></img></ViewButtonIcon>
            }
          </ProtectedFeature>
          <ProtectedFeature
            feature={Feature.OpenPropetyInfoDocument}
            contentsIfNoAccess={<></>}
          >

            <ViewButtonIcon
              className="no-print"
              onClick={() =>
                window.open(
                  `/propertyinfo/?latLng=${effectiveLabelLatLng?.latlng?.lat},${effectiveLabelLatLng?.latlng?.lng}`,
                  "_blank"
                )
              }
              title={Localization.getText("Popout")}
            >
              <FiCopy />
            </ViewButtonIcon>
          </ProtectedFeature>
          <ProtectedFeature
            feature={Feature.PrintPropertyInfo}
            contentsIfNoAccess={<></>}
          >
            <>
              {!(isLoading) ? (
                <>
                  <ViewButtonIcon
                    className="no-print"
                    onClick={() => {
                      setTimeout(() => {
                        print();
                      }, 100)
                    }}
                    title={Localization.getText("Print")}
                  >
                    <FiPrinter />
                  </ViewButtonIcon>
                </>
              ) : (
                <Spinner size="sm" />
              )}
            </>
          </ProtectedFeature>
          {fold}
        </>
      }
    >
      {/* <div className="mit-printable-modal" > */}
      {/* <PrintHeader/> */}
      <ADModalBodyProp>
        {(effectiveLabelLatLng?.latlng && (
          <PropertyInfoView
            key={effectiveLabelLatLng.latlng.lat.toString()}
            foldetUd={!propertyDefaultCollapsed}
            cols={1}
            labelLatLng={effectiveLabelLatLng}
            callBackOnLoaded={(data: PropertyData) => {
              setIsLoading(false);
              setPropertyData(data);
            }}
            allowExtendedInformation={true}
          />
        )) ||
          "Missing Dot"}
      </ADModalBodyProp>
    </AdvancedDragModal>
  );
}
